import {Link} from 'react-router';
import React, {useEffect, useState} from 'react';
import {
    CollectionSuggestionMinimal,
    SuggestedProblemEditStatusEnum,
    CollectionSuggestionsCriteria,
    CollectionSuggestionTypeEnum,
} from '@types';
import {
    useDeleteCollectionSuggestionMutation,
    useFetchCollectionSuggestionsQuery,
    useCountCollectionSuggestionsQuery,
    useResolveCollectionSuggestionMutation,
} from '@api';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import {ConfirmModal} from './modals/ConfirmModal';
import {CustomPagination, Loading, NothingHere, UserItem} from '@components';
import {useSelector} from '@utils';
import {NumberParam, useQueryParam} from 'use-query-params';
import {isEmpty} from 'lodash';
import {useTranslation} from 'react-i18next';
import {Table, Button, Form} from '@goproblems/ui';
import {useTheme} from '@components/theme/useTheme';
import {CollectionSuggestionsList} from './suggestions/CollectionSuggestionsList';
import {
    SuggestionStatusFilters,
    type SuggestionListLayout,
} from './suggestions/SuggestionEntry';

const StatusBadge = ({status}: {status: SuggestedProblemEditStatusEnum}) => {
    const {t} = useTranslation();
    const statusClass: Record<SuggestedProblemEditStatusEnum, string> = {
        [SuggestedProblemEditStatusEnum.OPEN]:
            'bg-blue-500 text-white px-2 py-1 rounded-sm text-sm uppercase',
        [SuggestedProblemEditStatusEnum.APPROVED]:
            'bg-green-700 text-white px-2 py-1 rounded-sm text-sm uppercase',
        [SuggestedProblemEditStatusEnum.REJECTED]:
            'bg-red-700 text-white px-2 py-1 rounded-sm text-sm uppercase',
    };
    const statusLabel: Record<SuggestedProblemEditStatusEnum, string> = {
        [SuggestedProblemEditStatusEnum.OPEN]: t('common.open'),
        [SuggestedProblemEditStatusEnum.APPROVED]: t('common.approved'),
        [SuggestedProblemEditStatusEnum.REJECTED]: t('common.rejected'),
    };

    return <span className={statusClass[status]}>{statusLabel[status]}</span>;
};

const TypeBadge = ({type}: {type: string}) => {
    const {t} = useTranslation();

    return (
        <span
            className={`px-2 py-1 rounded-sm text-sm uppercase text-white ${
                type === CollectionSuggestionTypeEnum.ADD
                    ? 'bg-green-700'
                    : 'bg-red-700'
            }`}>
            {type === CollectionSuggestionTypeEnum.ADD
                ? t('common.add')
                : t('common.remove')}
        </span>
    );
};

export const CollectionSuggestionsDataList = ({
    criteria: criteriaProp = {},
    header,
    embedded = false,
    renderLayout,
    showDeleteButton = true,
    showResolveButton = false,
    showCollectionColumn = true,
    showSuggestedByColumn = true,
}: {
    criteria?: CollectionSuggestionsCriteria;
    header?: string;
    embedded?: boolean;
    renderLayout?: SuggestionListLayout;
    showResolveButton?: boolean;
    showDeleteButton?: boolean;
    showCollectionColumn?: boolean;
    showSuggestedByColumn?: boolean;
}) => {
    const {user: authUser} = useSelector(state => state.auth);
    const theme = useTheme();
    const {t} = useTranslation();

    const [deleteCollectionSuggestion, {isLoading: isDeleting}] =
        useDeleteCollectionSuggestionMutation();
    const [resolveCollectionSuggestion, {isLoading: isResolvingSuggestion}] =
        useResolveCollectionSuggestionMutation();

    const [itemToDelete, setItemToDelete] = React.useState<number | null>(null);
    const [commentingItemId, setCommentingItemId] = React.useState<
        number | null
    >(null);
    const [resolutionComment, setResolutionComment] = useState('');
    const [paramCollectionId] = useQueryParam('collectionId', NumberParam);

    const [items, setItems] = useState<CollectionSuggestionMinimal[]>([]);
    const [offset, setOffset] = useState(0);
    const [status, setStatus] = useState<SuggestedProblemEditStatusEnum>(
        SuggestedProblemEditStatusEnum.OPEN
    );

    const textSecondaryClass =
        theme === 'dark' ? 'text-neutral-400' : 'text-neutral-600';
    const textSmallSecondaryClass = `text-sm ${textSecondaryClass}`;
    const actionButtonClass =
        'inline-flex items-center rounded-sm px-2 py-1 text-sm leading-5';
    const moreActionButtonClass =
        theme === 'dark'
            ? 'bg-neutral-600 text-white hover:bg-neutral-700 disabled:bg-neutral-500'
            : 'bg-neutral-200 text-neutral-800 hover:bg-neutral-300 disabled:bg-neutral-100 disabled:text-neutral-400';

    const handlePageChange = (offset: number) => {
        setOffset(offset);
    };

    const criteria: CollectionSuggestionsCriteria = {
        limit: 10,
        offset: 0,
        sortDirection: 'desc',
        sortBy: 'id',
        status,
        collectionId: paramCollectionId ?? undefined,
        ...criteriaProp,
    };

    const {
        data: suggestionsData,
        isFetching,
        refetch,
    } = useFetchCollectionSuggestionsQuery(
        {criteria: {...criteria, offset}},
        {refetchOnMountOrArgChange: true}
    );

    const {data: countsData, refetch: refetchCounts} =
        useCountCollectionSuggestionsQuery(
            {criteria: {...criteria, status: undefined}},
            {refetchOnMountOrArgChange: true}
        );

    useEffect(() => {
        if (suggestionsData) {
            setItems(suggestionsData.entries);
        }
    }, [suggestionsData]);

    const handleResolve = async (
        item: CollectionSuggestionMinimal,
        action: 'approve' | 'reject',
        comment = ''
    ) => {
        try {
            await resolveCollectionSuggestion({
                id: item.id,
                status:
                    action === 'approve'
                        ? SuggestedProblemEditStatusEnum.APPROVED
                        : SuggestedProblemEditStatusEnum.REJECTED,
                resolutionComment: comment,
            }).unwrap();
            toast.success(
                action === 'approve'
                    ? t('collection_suggestions.suggestion_approved')
                    : t('collection_suggestions.suggestion_rejected'),
                {autoClose: 1500}
            );
            setCommentingItemId(null);
            setResolutionComment('');
            void refetch();
            void refetchCounts();
            return true;
        } catch (error) {
            const message =
                (error as {data?: {message?: string}})?.data?.message ??
                t('collection_suggestions.failed_to_resolve_suggestion');
            toast.error(message);
            return false;
        }
    };

    const canApprove = (item: CollectionSuggestionMinimal): boolean => {
        if (
            item.type === CollectionSuggestionTypeEnum.ADD &&
            item.problemInCollection === true
        )
            return false;
        if (
            item.type === CollectionSuggestionTypeEnum.REMOVE &&
            item.problemInCollection === false
        )
            return false;
        return true;
    };

    const tdClassName = (item: CollectionSuggestionMinimal, className = '') => {
        return !isEmpty(item.comment) || !isEmpty(item.resolutionComment)
            ? `!border-b-0 ${className}`
            : `${className}`;
    };

    const filters = (
        <SuggestionStatusFilters
            value={status}
            counts={countsData}
            onChange={value => {
                setStatus(value);
                setOffset(0);
            }}
        />
    );

    const content = (
        <div
            className={`${embedded ? 'suggestion-list-embedded' : 'suggestion-list'} w-full flex flex-col`}>
            <ConfirmModal
                show={itemToDelete !== null}
                description={t(
                    'suggestions.are_you_sure_you_want_to_delete_this_suggestion',
                    {id: `#${itemToDelete}`}
                )}
                okVariant="danger"
                okText={t('common.delete')}
                onHide={() => setItemToDelete(null)}
                onOk={async () => {
                    if (itemToDelete === null || isDeleting) return;
                    try {
                        await deleteCollectionSuggestion(itemToDelete).unwrap();
                        toast.success(t('suggestions.deleted_successfully'), {
                            autoClose: 1000,
                        });
                        setItemToDelete(null);
                        void refetch();
                        void refetchCounts();
                    } catch {
                        toast.error(t('suggestions.failed_to_delete'));
                    }
                }}
            />
            {embedded ? (
                !renderLayout && filters
            ) : (
                <div className="suggestion-list-heading flex flex-wrap items-center justify-between gap-3 border-bottom px-3 py-2">
                    {!embedded && (
                        <span className="font-semibold">
                            {header ??
                                t('problem_collection.collection_suggestions')}
                        </span>
                    )}
                    <div
                        className="suggestion-status-filters flex flex-wrap gap-1"
                        role="group"
                        aria-label={t('suggestions.status')}>
                        {[
                            {
                                value: SuggestedProblemEditStatusEnum.OPEN,
                                count: countsData?.open ?? 0,
                                label: t('common.open'),
                            },
                            {
                                value: SuggestedProblemEditStatusEnum.APPROVED,
                                count: countsData?.approved ?? 0,
                                label: t('common.approved'),
                            },
                            {
                                value: SuggestedProblemEditStatusEnum.REJECTED,
                                count: countsData?.rejected ?? 0,
                                label: t('common.rejected'),
                            },
                        ].map(item => (
                            <button
                                key={item.value}
                                type="button"
                                aria-pressed={status === item.value}
                                className={`rounded px-2 py-1 text-sm transition-colors ${status === item.value ? 'bg-neutral-100 font-semibold dark:bg-[var(--ui-surface-muted)]' : 'text-muted hover:bg-neutral-50 dark:hover:bg-[var(--ui-surface-muted)]'}`}
                                onClick={() => {
                                    setStatus(item.value);
                                    setOffset(0);
                                }}>
                                {item.count} {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {isFetching && <Loading className="p-5" />}
            {!isFetching && items.length === 0 && (
                <div className="suggestion-list-empty mt-4">
                    <NothingHere />
                </div>
            )}
            {!isFetching &&
                items.length > 0 &&
                (embedded ? (
                    <CollectionSuggestionsList
                        items={items}
                        showDeleteButton={showDeleteButton}
                        showResolveButton={showResolveButton}
                        showCollectionColumn={showCollectionColumn}
                        showSuggestedByColumn={showSuggestedByColumn}
                        isDeleting={isDeleting}
                        isResolving={isResolvingSuggestion}
                        canApprove={canApprove}
                        onDelete={setItemToDelete}
                        onResolve={handleResolve}
                    />
                ) : (
                    <Table className="mb-0" responsive>
                        <thead>
                            <tr>
                                <th>{t('common.id')}</th>
                                <th>{t('common.problem')}</th>
                                {showCollectionColumn && (
                                    <th>
                                        {t(
                                            'problem_collection.title_collection'
                                        )}
                                    </th>
                                )}
                                <th>{t('problem_collection.type')}</th>
                                <th>{t('common.status')}</th>
                                {showSuggestedByColumn && (
                                    <th>{t('common.suggested_by')}</th>
                                )}
                                <th>{t('common.created_at')}</th>
                                <th>{t('common.resolved_by')}</th>
                                <th>{t('common.resolved_at')}</th>
                                <th>{t('common.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <React.Fragment key={item.id}>
                                    <tr>
                                        <td className={tdClassName(item)}>
                                            {item.id}
                                        </td>
                                        <td className={tdClassName(item)}>
                                            <Link
                                                to={
                                                    '/problems/' +
                                                    item.problem.id
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <img
                                                    src={item.problem.imageUrl}
                                                    alt={t(
                                                        'problem_collection.board_thumbnail'
                                                    )}
                                                    className="w-12 h-12"
                                                />
                                            </Link>
                                        </td>
                                        {showCollectionColumn && (
                                            <td className={tdClassName(item)}>
                                                <Link
                                                    to={`/collections/${item.collection.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {item.collection.name}
                                                </Link>
                                            </td>
                                        )}
                                        <td className={tdClassName(item)}>
                                            <TypeBadge type={item.type} />
                                        </td>
                                        <td
                                            className={`w-20 ${tdClassName(item)}`}>
                                            <StatusBadge status={item.status} />
                                        </td>
                                        {showSuggestedByColumn && (
                                            <td
                                                className={`min-w-[200px] ${tdClassName(item)}`}>
                                                <UserItem
                                                    user={item.user}
                                                    textClassName={'!mx-1'}
                                                    avatarClassName={'!ml-0'}
                                                />
                                            </td>
                                        )}
                                        <td className={tdClassName(item)}>
                                            {dayjs(item.createdAt).fromNow()}
                                        </td>
                                        <td className={tdClassName(item)}>
                                            {item.resolvedBy?.name || '-'}
                                        </td>
                                        <td className={tdClassName(item)}>
                                            {item.resolvedAt
                                                ? dayjs(
                                                      item.resolvedAt
                                                  ).fromNow()
                                                : '-'}
                                        </td>
                                        <td className={tdClassName(item)}>
                                            <div className="flex gap-2">
                                                {showResolveButton &&
                                                    item.status ===
                                                        SuggestedProblemEditStatusEnum.OPEN && (
                                                        <>
                                                            <button
                                                                className={`text-white px-2 py-1 rounded-sm text-sm ${
                                                                    canApprove(
                                                                        item
                                                                    )
                                                                        ? `${actionButtonClass} bg-green-500 hover:bg-green-600`
                                                                        : `${actionButtonClass} bg-neutral-400 cursor-not-allowed`
                                                                }`}
                                                                disabled={
                                                                    !canApprove(
                                                                        item
                                                                    ) ||
                                                                    isResolvingSuggestion
                                                                }
                                                                title={
                                                                    !canApprove(
                                                                        item
                                                                    )
                                                                        ? item.type ===
                                                                          CollectionSuggestionTypeEnum.ADD
                                                                            ? t(
                                                                                  'collection_suggestions.problem_already_in_collection'
                                                                              )
                                                                            : t(
                                                                                  'collection_suggestions.problem_not_in_collection'
                                                                              )
                                                                        : ''
                                                                }
                                                                onClick={() =>
                                                                    handleResolve(
                                                                        item,
                                                                        'approve'
                                                                    )
                                                                }>
                                                                {t(
                                                                    'suggestions.approve'
                                                                )}
                                                            </button>
                                                            <button
                                                                className={`${actionButtonClass} bg-red-500 text-white hover:bg-red-600`}
                                                                disabled={
                                                                    isResolvingSuggestion
                                                                }
                                                                onClick={() =>
                                                                    handleResolve(
                                                                        item,
                                                                        'reject'
                                                                    )
                                                                }>
                                                                {t(
                                                                    'suggestions.reject'
                                                                )}
                                                            </button>
                                                            <button
                                                                className={`${actionButtonClass} min-w-9 justify-center ${moreActionButtonClass}`}
                                                                disabled={
                                                                    isResolvingSuggestion
                                                                }
                                                                title={t(
                                                                    'collection_suggestions.resolve_with_comment'
                                                                )}
                                                                onClick={() => {
                                                                    setCommentingItemId(
                                                                        commentingItemId ===
                                                                            item.id
                                                                            ? null
                                                                            : item.id
                                                                    );
                                                                    setResolutionComment(
                                                                        ''
                                                                    );
                                                                }}>
                                                                ...
                                                            </button>
                                                        </>
                                                    )}
                                                {showDeleteButton &&
                                                    item.status ===
                                                        SuggestedProblemEditStatusEnum.OPEN &&
                                                    item.user.id ===
                                                        authUser?.id && (
                                                        <button
                                                            className="bg-red-700 text-white px-2 py-1 rounded-sm text-sm"
                                                            onClick={() => {
                                                                setItemToDelete(
                                                                    item.id
                                                                );
                                                            }}>
                                                            {t('common.delete')}
                                                        </button>
                                                    )}
                                                {!showResolveButton &&
                                                    !showDeleteButton &&
                                                    '-'}
                                                {(showResolveButton ||
                                                    showDeleteButton) &&
                                                    item.status !==
                                                        SuggestedProblemEditStatusEnum.OPEN &&
                                                    '-'}
                                            </div>
                                        </td>
                                    </tr>
                                    {/* Resolve inline form */}
                                    {commentingItemId === item.id && (
                                        <tr>
                                            <td></td>
                                            <td colSpan={9}>
                                                <div className="flex items-center gap-2 py-2">
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={t(
                                                            'collection_suggestions.resolution_comment_optional'
                                                        )}
                                                        value={
                                                            resolutionComment
                                                        }
                                                        onChange={e =>
                                                            setResolutionComment(
                                                                e.target.value
                                                            )
                                                        }
                                                        size="sm"
                                                        className="max-w-xs"
                                                    />
                                                    <Button
                                                        size="sm"
                                                        variant="success"
                                                        disabled={
                                                            !canApprove(item) ||
                                                            isResolvingSuggestion
                                                        }
                                                        title={
                                                            !canApprove(item)
                                                                ? item.type ===
                                                                  CollectionSuggestionTypeEnum.ADD
                                                                    ? t(
                                                                          'collection_suggestions.problem_already_in_collection'
                                                                      )
                                                                    : t(
                                                                          'collection_suggestions.problem_not_in_collection'
                                                                      )
                                                                : ''
                                                        }
                                                        onClick={() =>
                                                            handleResolve(
                                                                item,
                                                                'approve',
                                                                resolutionComment
                                                            )
                                                        }>
                                                        {t(
                                                            'collection_suggestions.confirm_approve'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        disabled={
                                                            isResolvingSuggestion
                                                        }
                                                        onClick={() =>
                                                            handleResolve(
                                                                item,
                                                                'reject',
                                                                resolutionComment
                                                            )
                                                        }>
                                                        {t(
                                                            'collection_suggestions.confirm_reject'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setCommentingItemId(
                                                                null
                                                            );
                                                            setResolutionComment(
                                                                ''
                                                            );
                                                        }}>
                                                        {t('common.cancel')}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {/* Comment / resolution comment row */}
                                    {(!isEmpty(item.comment) ||
                                        !isEmpty(item.resolutionComment)) &&
                                        commentingItemId !== item.id && (
                                            <tr>
                                                <td></td>
                                                <td
                                                    colSpan={9}
                                                    className="border-t-0">
                                                    <div className="flex flex-col gap-2 mb-2">
                                                        {!isEmpty(
                                                            item.comment
                                                        ) && (
                                                            <div
                                                                className={
                                                                    textSmallSecondaryClass
                                                                }>
                                                                <span className="font-semibold mr-1">
                                                                    {t(
                                                                        'common.comment'
                                                                    )}
                                                                    :
                                                                </span>
                                                                {item.comment}
                                                            </div>
                                                        )}
                                                        {!isEmpty(
                                                            item.resolutionComment
                                                        ) && (
                                                            <div
                                                                className={
                                                                    textSmallSecondaryClass
                                                                }>
                                                                <span className="font-semibold mr-1">
                                                                    {t(
                                                                        'suggestions.resolution_comment'
                                                                    )}
                                                                    :
                                                                </span>
                                                                {
                                                                    item.resolutionComment
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                ))}
            {suggestionsData && items.length > 0 && (
                <div
                    className={
                        embedded ? 'suggestion-list-pagination' : 'mt-4 mb-2'
                    }>
                    <CustomPagination
                        handlePageChange={handlePageChange}
                        totalRecords={suggestionsData.totalRecords}
                        recordsPerPage={10}
                        offset={offset}
                    />
                </div>
            )}
        </div>
    );

    return renderLayout ? renderLayout({filters, children: content}) : content;
};
